
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import "./TableTennis.css";

const TIMEZONE = "Africa/Dar_es_Salaam";
const MATCHES_COLLECTION = "tabletennis";
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const INITIAL_VISIBLE = 30;
const CACHE_KEY = "tabletennis_matches_cache_v1";

function getTodayString() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function addDays(dateString, amount) {
  const [year, month, day] = dateString.split("-").map(Number);

  return new Date(
    Date.UTC(year, month - 1, day + amount, 12)
  ).toISOString().slice(0, 10);
}

function parseDate(date, time = "") {
  if (!date) return 0;

  try {
    if (typeof date.toDate === "function") {
      return date.toDate().getTime();
    }

    if (date instanceof Date) {
      return date.getTime();
    }

    if (typeof date === "object" && date.seconds != null) {
      return Number(date.seconds) * 1000;
    }

    const dateString = String(date).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const cleanTime = /^\d{1,2}:\d{2}$/.test(String(time))
        ? String(time).replace(/^(\d):/, "0$1:")
        : "00:00";

      const timestamp = new Date(
        `${dateString}T${cleanTime}:00+03:00`
      ).getTime();

      return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    const timestamp = new Date(
      dateString.replace(" ", "T")
    ).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
  } catch {
    return 0;
  }
}

function getMatchDateString(match) {
  const timestamp = parseDate(match.date, match.time);
  if (!timestamp) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(timestamp));

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function formatDateHeader(dateString) {
  if (!dateString) return "";

  const timestamp = new Date(
    `${dateString}T12:00:00+03:00`
  ).getTime();

  return new Date(timestamp).toLocaleDateString("en-GB", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(date, time) {
  const timestamp = parseDate(date, time);
  if (!timestamp) return "-";

  return new Date(timestamp).toLocaleString("en-GB", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function readCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
}

function saveCache(matches) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(matches));
  } catch (error) {
    console.warn("Table Tennis cache could not be saved", error);
  }
}

function filterAndSortMatches(rawMatches, now = Date.now()) {
  if (!Array.isArray(rawMatches)) return [];

  return rawMatches
    .filter((match) => {
      const timestamp = parseDate(match.date, match.time);

      return (
        timestamp > now + FIFTEEN_MINUTES &&
        timestamp < now + THIRTY_DAYS
      );
    })
    .sort(
      (a, b) =>
        parseDate(a.date, a.time) -
        parseDate(b.date, b.time)
    );
}

export default function TableTennis() {
  const navigate = useNavigate();

  const [matches, setMatches] = useState(readCache);
  const [loading, setLoading] = useState(() => readCache().length === 0);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("next");
  const [dayFilter, setDayFilter] = useState("today");
  const [visible, setVisible] = useState(INITIAL_VISIBLE);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMatches() {
      try {
        const snapshot = await getDocs(
          collection(db, MATCHES_COLLECTION)
        );

        if (cancelled) return;

        const all = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        const future = filterAndSortMatches(all);

        setMatches(future);
        saveCache(future);
        setError("");
      } catch (err) {
        console.error("TABLE TENNIS FIREBASE ERROR:", err);

        if (!cancelled) {
          setError(
            "Imeshindikana kupata mechi kutoka Firebase. Jaribu Refresh."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadMatches();

    const refreshTimer = setInterval(loadMatches, 60000);

    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
    };
  }, []);

  const futureMatches = useMemo(
    () =>
      matches
        .filter(
          (match) =>
            parseDate(match.date, match.time) >
            now + FIFTEEN_MINUTES
        )
        .sort(
          (a, b) =>
            parseDate(a.date, a.time) -
            parseDate(b.date, b.time)
        ),
    [matches, now]
  );

  const showMatches = useMemo(() => {
    let result = [...futureMatches];

    if (tab === "featured") {
      result = result.slice(0, 50);
    }

    if (search.trim()) {
      const query = search.trim().toLowerCase();

      result = result.filter((match) => {
        const home = String(
          match.homeTeam || match.A || match.teamA || ""
        ).toLowerCase();

        const away = String(
          match.awayTeam || match.B || match.teamB || ""
        ).toLowerCase();

        const league = String(
          match.league || match.competition || ""
        ).toLowerCase();

        const country = String(
          match.country || match.countryName || ""
        ).toLowerCase();

        return (
          home.includes(query) ||
          away.includes(query) ||
          league.includes(query) ||
          country.includes(query)
        );
      });
    }

    if (dayFilter !== "all") {
      const targetDate =
        dayFilter === "today"
          ? getTodayString()
          : addDays(getTodayString(), 1);

      result = result.filter(
        (match) => getMatchDateString(match) === targetDate
      );
    }

    return result.sort(
      (a, b) =>
        parseDate(a.date, a.time) -
        parseDate(b.date, b.time)
    );
  }, [futureMatches, tab, search, dayFilter]);

  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [tab, search, dayFilter]);

  const grouped = useMemo(() => {
    const dateGroups = {};

    showMatches.forEach((match) => {
      const dateKey = getMatchDateString(match);
      if (!dateKey) return;

      const league = match.league || match.competition || "Other";

      if (!dateGroups[dateKey]) dateGroups[dateKey] = {};

      if (!dateGroups[dateKey][league]) {
        dateGroups[dateKey][league] = [];
      }

      dateGroups[dateKey][league].push(match);
    });

    return Object.keys(dateGroups)
      .sort()
      .map((dateKey) => ({
        dateKey,
        leagues: dateGroups[dateKey],
      }));
  }, [showMatches]);

  function renderMatch(match, index) {
    const teamA =
      match.homeTeam || match.A || match.teamA || "Player A";

    const teamB =
      match.awayTeam || match.B || match.teamB || "Player B";

    const league = match.league || match.competition || "Table Tennis";
    const country = match.country || match.countryName || "Table Tennis";

    return (
      <div
        className="sports-match-card"
        key={match.id || `${teamA}-${teamB}-${index}`}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (match.id) {
            navigate(`/otherbetscore/${match.id}`, { state: match });
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            if (match.id) {
              navigate(`/otherbetscore/${match.id}`, { state: match });
            }
          }
        }}
      >
        <div className="sports-match-info">
          <div className="sports-teams">
            <span className="sports-team-name">{teamA}</span>
            <span className="sports-vs">VS</span>
            <span className="sports-team-name">{teamB}</span>
          </div>

          <div className="sports-match-league">{league}</div>

          <div className="sports-match-meta">
            <span className="sports-time">
              {formatTime(match.date, match.time)}
            </span>
          </div>
        </div>

        <div className="sports-match-country">
          <span className="sports-country-icon">🏓</span>
          <span className="sports-country-name">{country}</span>
        </div>
      </div>
    );
  }

  let renderedCount = 0;
  const visibleGrouped = [];

  for (const dateGroup of grouped) {
    if (renderedCount >= visible) break;

    const dateLeagues = [];

    for (const [league, leagueMatches] of Object.entries(
      dateGroup.leagues
    )) {
      if (renderedCount >= visible) break;

      const remaining = visible - renderedCount;
      const matchesToShow = leagueMatches.slice(0, remaining);

      if (matchesToShow.length) {
        dateLeagues.push({ league, matches: matchesToShow });
        renderedCount += matchesToShow.length;
      }
    }

    if (dateLeagues.length) {
      visibleGrouped.push({
        dateKey: dateGroup.dateKey,
        leagues: dateLeagues,
      });
    }
  }

  return (
    <main className="sports-page">
      <div className="sports-page-inner">
        <div className="sports-league-tabs">
          <div className="sports-date-title">
            <span className="sports-date-dot" />
            Table Tennis Matches
          </div>
        </div>

        <div className="sports-controls">
          <div className="sports-select">
            <button
              type="button"
              className={`sports-select-box ${
                tab === "next" ? "active" : ""
              }`}
              onClick={() => setTab("next")}
            >
              <span className="count">{futureMatches.length}</span>
              <span className="label">Next</span>
            </button>

            <button
              type="button"
              className={`sports-select-box ${
                tab === "featured" ? "active" : ""
              }`}
              onClick={() => setTab("featured")}
            >
              <span className="count">
                {Math.min(futureMatches.length, 50)}
              </span>
              <span className="label">Featured</span>
            </button>

            <button
              type="button"
              className={`sports-select-box ${
                tab === "all" ? "active" : ""
              }`}
              onClick={() => setTab("all")}
            >
              <span className="count">{futureMatches.length}</span>
              <span className="label">All</span>
            </button>
          </div>

          <div className="sports-search-wrap">
            <span className="sports-search-icon">⌕</span>
            <input
              className="sports-search-input"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search table tennis player or league..."
            />
          </div>

          <div className="sports-day-tabs">
            <button
              type="button"
              className={dayFilter === "today" ? "active" : ""}
              onClick={() => setDayFilter("today")}
            >
              Today
            </button>

            <button
              type="button"
              className={dayFilter === "tomorrow" ? "active" : ""}
              onClick={() => setDayFilter("tomorrow")}
            >
              Tomorrow
            </button>

            <button
              type="button"
              className={dayFilter === "all" ? "active" : ""}
              onClick={() => setDayFilter("all")}
            >
              All Days
            </button>
          </div>
        </div>

        {error && (
          <div className="sports-empty-state">
            <strong>{error}</strong>
            <button
              className="sports-more-btn"
              type="button"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        )}

        {loading ? (
          <div className="sports-loading-overlay">
            <div className="sports-loading-box">
              <div className="sports-loading-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        ) : showMatches.length === 0 ? (
          <div className="sports-empty-state">
            <div className="sports-empty-icon">🏓</div>
            <strong>No table tennis matches found</strong>
            <small>
              Hakuna mechi za Table Tennis zilizopatikana kwa kichujio hiki.
            </small>
          </div>
        ) : (
          <div className="sports-matches-container">
            {visibleGrouped.map((dateGroup) => (
              <section
                className="sports-date-section"
                key={dateGroup.dateKey}
              >
                <h3 className="sports-date-title">
                  <span className="sports-date-dot" />
                  {formatDateHeader(dateGroup.dateKey)}
                </h3>

                {dateGroup.leagues.map(({ league, matches: leagueMatches }) => (
                  <section
                    className="sports-league-section"
                    key={`${dateGroup.dateKey}-${league}`}
                  >
                    <h4 className="sports-league-title">
                      <span className="sports-league-dot" />
                      {league}
                    </h4>

                    {leagueMatches.map(renderMatch)}
                  </section>
                ))}
              </section>
            ))}

            {showMatches.length > visible && (
              <button
                type="button"
                className="sports-more-btn"
                onClick={() =>
                  setVisible((current) => current + INITIAL_VISIBLE)
                }
              >
                View More Matches ({showMatches.length - visible} remaining)
              </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
